import { useMemo } from "react";
import { useDispatch } from "react-redux";

import { Status } from "@/B2C/features/Policies/components/Status";
import { DataTable } from "@/shared/components/DataTable/DataTable";
import type { Channel } from "@/shared/redux/reducers/channelSlice";
import { openRightPanel } from "@/shared/redux/reducers/uiSlice";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { textFormatter } from "@/shared/utils/textFormatter";
import DetailSidePanel from "./components/DetailSidePanel";
import {
  REVIEW_QUEUE_NON_SORTABLE_COLUMNS,
  REVIEW_QUEUE_TABLE_HEADERS,
} from "./constants/reviewQueue.constants";

type ReviewQueueRow = Channel & {
  recordedBy: string;
  submittedOn: string;
};

function ReviewQueuePage() {
  const dispatch = useDispatch();
  const {
    reviewQueue: { reviewQueueList },
  } = usePosterReducers();

  const reviewQueueRows = useMemo<ReviewQueueRow[]>(
    () => reviewQueueList.data,
    [reviewQueueList.data]
  );

  const headers = useMemo(() => [...REVIEW_QUEUE_TABLE_HEADERS], []);

  const renderRow = (row: ReviewQueueRow) => (
    <>
      <td className="px-4 py-3 text-sm text-gray-700">
        <div className="font-medium text-gray-800">{textFormatter(row.name)}</div>
        <div className="text-xs text-gray-500">{row.channelCode}</div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">{row.type}</td>
      <td className="px-4 py-3 text-sm text-gray-700">{row.recordedBy}</td>
      <td className="px-4 py-3 text-sm text-gray-700">{row.submittedOn}</td>
      <td className="px-4 py-3 text-sm">
        <Status status={row.status} />
      </td>
    </>
  );

  const handleRowClick = (row: ReviewQueueRow) => {
    dispatch(
      openRightPanel({
        name: "review-queue-detail",
        title: row.name,
        data: row,
        className: "max-w-md",
        bodyClassName: "p-0",
        children: (panelData: ReviewQueueRow) => <DetailSidePanel panelData={panelData} />,
      })
    );
  };

  return (
    <DataTable
      headers={headers}
      rows={reviewQueueRows}
      renderRow={renderRow}
      onRowClick={handleRowClick}
      searchPlaceholder="Search channels, applications, users"
      pageSize={10}
      totalCount={reviewQueueRows.length}
      showSearch
      isPagination
      sortable
      nonSortableColumns={REVIEW_QUEUE_NON_SORTABLE_COLUMNS}
    />
  );
}

export default ReviewQueuePage;
